import { useBoolean, useToast } from '@chakra-ui/react'
import * as crypto from 'crypto-js'
import fileDownload from 'js-file-download'
import {
  createContext,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { AiOutlineBarChart } from 'react-icons/ai'
import Swal from 'sweetalert2'
import { useImmer } from 'use-immer'

import useSessions from '@hook/shared/use-sessions'

import SapHistoricModalWrapper from '@component/Layout/Tools/SapHistoricModalWrapper'
import ReabPeriodChart from '@component/Modules/Mrp/Modals/ReabPeriodChart'

import { parseReabMrpData } from '@helper/grid'
import { parseReabSapMrpData } from '@helper/grid/mrp/reab-sap'
import paramsHelper from '@helper/params'
import {
  decrypterHeaderStorange,
  encrypteHeaderStorange,
  mergeStoredHeaders
} from '@helper/util/cryptoStorange'
import generic from '@service/generic'
import mrp from '@service/mrp'

import { ILayoutFilter } from '@base/@types/components/Layout/Filters'
import {
  ITableGridAction,
  ITableHeader
} from '@base/@types/components/Layout/TableGrid'
import { ILayoutToolsAction } from '@base/@types/components/Layout/Tools'
import {
  IMrpReabContext,
  IParams,
  IUpdateParams,
  Resumo,
  MrpReabCalc,
  Discount,
  IParamsSap,
  IUpdateParamsSap
} from '@base/@types/providers/Mrp/ReabProvider'
import mrpReabHeader from '@base/variables/headers/mrp/reab'

const MrpReabProvider: FC = ({ children }) => {
  const toast = useToast()

  const { register } = useSessions()

  const [loading, setLoading] = useState(false)
  const [fetching, setFetching] = useState(false)
  const [filters, setFilters] = useBoolean(true)

  const [query, setQuery] = useState('')
  const [filename, setFilename] = useState('MRP Reab. Emergencial')

  const [selected, setSelected] = useState<any>()
  const [_ready, setReady] = useState<boolean>(false)
  const [sapFlag, setSapFlag] = useState<boolean | null>(null)

  const [data, updateData] = useImmer<any[]>([])
  const [dataCards, updateDataCards] = useImmer<Resumo>({
    produtos: 0,
    peso: 0,
    indicadorCurva: []
  })

  const [calc] = useImmer<MrpReabCalc>({
    total: {
      products: 0,
      weight: 0
    },
    a: {
      percentage: 0,
      quantity: 0
    },
    b: {
      percentage: 0,
      quantity: 0
    },
    c: {
      percentage: 0,
      quantity: 0
    }
  })

  const [params, updateParams] = useImmer<IParams>({
    produtosIds: [],
    fornecedoresIds: [],
    lojasIds: [],
    empresaIds: [],
    curvaAbc: []
  })

  const filtersList = useMemo<ILayoutFilter[]>(() => {
    return [
      {
        id: 'lojasIds',
        label: 'Centro',
        filterId: 'centers',
        type: 'select',
        isMulti: true
      },
      {
        id: 'produtosIds',
        label: 'Produto',
        type: 'select-api-post',
        apiUrl: '/v1/Produto/Compras',
        isMulti: true
      },
      {
        id: 'empresaIds',
        label: 'Empresas',
        filterId: 'companies',

        type: 'select',
        isMulti: true
      },
      {
        id: 'fornecedoresIds',
        label: 'Fornecedor',
        filterId: 'providersForPurchase',
        type: 'select',
        isMulti: true
      },
      {
        id: 'curvaAbc',
        label: 'Curva',
        filterId: 'abcCurves',
        isMulti: true,
        type: 'select',
        limit: 1
      }
    ]
  }, [])

  const [supplyMethod, updateSupplyMethod] = useImmer<any[]>([])
  const [discountSupply, updateDiscountSupply] = useImmer<Discount>({
    id: 0,
    desconto: 0,
    fornecedorId: 0,
    metodoPagamentoId: 0
  })

  const [paramsSap, updateSapParams] = useImmer<IParamsSap>({
    desconto: '',
    metodoPagamento: []
  })

  const [currentOrderCode, setCurrentOrderCode] = useState('')
  const [orderCreationStatus, setOrderCreationStatus] = useState('')
  const handleClearOrderCode = () => {
    setCurrentOrderCode('')
    setOrderCreationStatus('Order code cleared.')
  }

  const isSapExportFlag = async () => {
    try {
      const { data: res } = await generic.getConfig(
        'FLAG_EXPORTAR_SAP_EMERGENCIAL'
      )
      const { data } = res
      if (data?.valor === 'true') {
        return true
      } else {
        return false
      }
    } catch {
      return false
    }
  }

  // A configuração salva entra já na inicialização do estado. Restaurá-la
  // depois, em efeito ou durante a busca, descartava a seleção que o usuário
  // tinha acabado de aplicar.
  const [headers, updateHeaders] = useImmer<ITableHeader[]>(() =>
    mergeStoredHeaders(
      mrpReabHeader,
      decrypterHeaderStorange('@mrpReabColumnsTable:')
    )
  )

  // Recebe os headers a gravar: fechar sobre `headers` persistia o valor
  // anterior ao toggle que acabou de ser aplicado.
  const handleHeaderLocalStorange = useCallback((header: ITableHeader[]) => {
    localStorage.removeItem('@mrpReabColumnsTable:')
    encrypteHeaderStorange('@mrpReabColumnsTable:', header)
  }, [])

  const toggleColumn = useCallback(
    (index: number, column: number) => {
      // Recipe do immer: o editor aplica vários toggles no mesmo lote do React
      // e recipes encadeiam sobre o resultado anterior. Derivar o próximo valor
      // de `headers` fazia só o último toggle do lote valer.
      updateHeaders(draft => {
        const col = draft[index]?.columns?.[column]
        if (col) {
          col.show = !col.show
          // Marcador estável de "configurada pelo usuário". Não pode alternar
          // junto com `show`, senão o editor perde o grupo de vista.
          col.notShowColumn = true
        }
      })
    },
    [updateHeaders]
  )

  // Persiste a configuração sempre que ela muda. Na montagem regrava o mesmo
  // valor que acabou de ser restaurado, então é inofensivo.
  useEffect(() => {
    handleHeaderLocalStorange(headers)
  }, [headers, handleHeaderLocalStorange])

  const actions = useMemo<ILayoutToolsAction[]>(() => {
    if (sapFlag === null) return [] // Aguarda a flag ser carregada antes de definir ações

    return [
      {
        type: 'purchaseSuppliers-createOrder-reab',
        data: data,
        onUpdate: null
      },
      // {
      //   type: 'sapHistoricReab'
      // },
      {
        type: 'export',
        exports: [
          {
            type: 'excel',
            entity: 'ReabMrp',
            data,
            headers,
            parser: parseReabMrpData
          },
          ...(sapFlag
            ? [
                {
                  type: 'sap' as const,
                  data,
                  entity: 'ReabMrp',
                  sapModal: true,
                  parser: parseReabSapMrpData
                }
              ]
            : [])
        ]
      },
      {
        type: 'columns',
        data: headers,
        onToggleColumn: toggleColumn
      }
    ]
  }, [toggleColumn, data, headers])

  const handleUpdateWanted = useCallback(
    async (idProduto: number, quant: number) => {
      let purchases = []
      try {
        purchases = data
        const purchase = purchases.filter(
          x => x.produtoCodigo === Number(idProduto)
        )[0]
        const idData = purchases.findIndex(
          (x: any) => x.produtoCodigo === Number(idProduto)
        )
        try {
          purchases = [...purchases] // important to create a copy, otherwise you'll modify state outside of setState call
          purchases[idData] = {
            ...purchase,
            desejada: {
              quantidade: quant,
              cobertura: purchase.desejada.cobertura,
              coberturaAoAdicionarUmPacote:
                purchase.desejada.coberturaAoAdicionarUmPacote,
              custo: purchase.desejada.custo,
              volume: purchase.desejada.volume,
              peso: purchase.desejada.peso,
              pedidos: purchase.desejada.pedidos,
              medidaVenda: purchase.desejada.medidaVenda,
              medidaEstoque: purchase.desejada.mediaEstoque,
              nivelServico: purchase.desejada.nivelServico
            }
          }

          const newData = purchases

          updateData(draft => {
            return draft.filter(item => item.status === 99).concat(newData)
          })
        } catch (e) {}
      } catch {}
      // setLoading(false)
    },
    [data, updateData]
  )
  const tableInteraction = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'icon',
        icon: <AiOutlineBarChart />,
        onSelect: setSelected
      }
      // { type: 'iconCalculo' } — oculto, manter para ativar futuramente
    ]
  }, [])

  const updateShowTableInTable = (data: any[]) => {
    updateData(draft => {
      return draft.filter(item => item.status === 99).concat(data)
    })

    // A configuração de colunas é restaurada na inicialização do estado.
    // Recarregá-la aqui descartava a seleção recém-aplicada pelo usuário.
  }

  const clearAllFilters = () => {
    updateParams({
      ...params,
      produtosIds: [],
      fornecedoresIds: [],
      lojasIds: [],
      empresaIds: [],
      curvaAbc: []
    })
    if (localStorage.getItem('mrpReabParams')) {
      localStorage.setItem(
        'mrpReabParams',
        crypto.AES.encrypt(
          JSON.stringify({
            ...params,
            produtosIds: [],
            fornecedoresIds: [],
            lojasIds: [],
            empresaIds: [],
            curvaAbc: []
          }),
          'CHAVE'
        ).toString()
      )
    }
  }

  const handleUpdateParams = ({ name, value }: IUpdateParams) => {
    updateParams(draft => {
      draft[name] = value
    })
    if (localStorage.getItem('mrpReabParams')) {
      const newParams: IParams[] = JSON.parse(
        crypto.AES.decrypt(
          localStorage.getItem('mrpReabParams'),
          'CHAVE'
        ).toString(crypto.enc.Utf8) || ''
      )

      localStorage.setItem(
        'mrpReabParams',
        crypto.AES.encrypt(
          JSON.stringify({ ...newParams, [name]: value }),
          'CHAVE'
        ).toString()
      )
    }
  }
  const startBackgroundPagination = useCallback(
    async (page: number, params: any) => {
      try {
        const { data: res } = await mrp.getReabData({
          page,
          params
        })

        const { data, pagination } = res

        updateData(draft => {
          return draft.concat(data)
        })

        if (pagination.totalPages === page) {
          setFetching(false)
        } else {
          startBackgroundPagination(page + 1, params)
        }
      } catch {}
    },
    [updateData]
  )

  const exportData = useCallback(async () => {
    Swal.fire({
      title: 'Exporta dados!',
      html: 'Aguarde um momento<b></b>.',
      timerProgressBar: true,
      timer: 99999,
      didOpen: () => {
        Swal.showLoading()
      }
    })
    try {
      const purshase = data
      const command = {
        lojaConfiguracaoGrid: {
          codigo: headers[0].columns[0] ? headers[0].columns[0].show : true,
          nome: headers[0].columns[0] ? headers[0].columns[1].show : true
        },
        centroDistribuicaoConfiguracaoGrid: {
          codigo: true,
          nome: true
        },
        produtoConfiguracaoGrid: {
          material: true,
          leadTime: true,
          intervaloCompra: true,
          codigo: true,
          nome: true,
          categoria: true,
          curvaABC: true,
          curvaABCEmpresa: true,
          status: true,
          loteMultiplo: true,
          grupoSeparacao: true
        },
        fornecedorConfiguracaoGrid: {
          codigo: true,
          nome: true
        },

        recomendacaoConfiguracaoGrid: {
          pedidos: true,
          coberturaMedia: true,
          quantidade: true,
          peso: true,
          custo: true,
          volume: true,
          tipoAlerta: true,
          nivelServico: true
        },
        compraConfiguracaoGrid: {
          pedidos: true,
          quantidade: true,
          coberturaMedia: true,
          peso: true,
          volume: true,
          desejada: true,
          custo: true,
          nivelServico: true
        },

        estoqueConfiguracaoGrid: {
          max: true,
          cd: true,
          disponivel: true,
          reservado: true,
          transito: true,
          dep16: true,
          outros: true,
          vendaMediaDiaria: true,
          vendaMediaMensal: true
        },

        estoqueReabEmergencialConfiguracaoGrid: {
          estoqueAtual: true,
          pedidosEmTransito: true,
          coberturaAtual: true,
          curvaABC: true,
          estoqueCD: true,
          quantidadeSugerida: true,
          peso: true
        },

        coberturaConfiguracaoGrid: {
          alvo: true,
          atual: true,
          transito: true,
          sugestao: true,
          desejada: true,
          diferenca: true
        }
      }
      const { data: res } = await mrp.postReabExport({
        params: {
          recomendacaoFornecimento: purshase,
          configuracaoGrid: command
        }
      })

      fileDownload(res, filename + '.csv')
      Swal.close()
    } catch (error: any) {
      const { data } = error.response
      const message =
        data.response?.status === 400
          ? data.response?.data?.data[0].message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'error',
        duration: 2000,
        isClosable: true
      })
      Swal.close()
    }
  }, [data, toast, filename, headers])
  const exportDataSap = useCallback(async () => {
    Swal.fire({
      title: 'Criando Pedidos!',
      html: 'Aguarde um momento<b></b>.',
      allowOutsideClick: false,
      allowEscapeKey: false,
      allowEnterKey: false,
      showConfirmButton: false,
      backdrop: true,
      didOpen: () => {
        Swal.showLoading()
      }
    })
    const purshase = data
    try {
      let obj = {}
      const unique = [
        ...new Set(
          data.map(item => item.estoque?.produtoFornecedor?.fornecedor?.codigo)
        )
      ]
      if (unique.length === 1) {
        obj = {
          recomendacaoFornecimentoCommand: {
            grid: purshase,
            resumo: dataCards
          },
          desconto:
            Number(paramsSap.desconto) !== 0
              ? Number(paramsSap.desconto)
              : null,
          formaDePagamento: paramsSap.metodoPagamento[0]
            ? paramsSap.metodoPagamento[0].value !== 0
              ? supplyMethod.filter(
                  x => x.id === paramsSap.metodoPagamento[0].value
                )[0].codigo
              : null
            : null
        }
      } else {
        obj = { recomendacaoFornecimentoCommand: purshase }
      }
      const { data: res, status } = await mrp.postReabExportSap({
        params: obj
      })

      // Verificação manual do status de resposta e conteúdo
      if (status !== 200 || res?.erro || res?.error || res?.statusCode >= 400) {
        throw new Error(res?.message || 'Erro ao processar pedido')
      }

      setCurrentOrderCode('102')
      setOrderCreationStatus('Order created manually: 103')
      toast({
        title: 'Sucesso.',
        description: 'Pedido criado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
      Swal.close()
    } catch (error: any) {
      Swal.close()
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'error',
        duration: 5000,
        isClosable: true
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, paramsSap.desconto, paramsSap.metodoPagamento, filename, toast])

  const exportDataSapCsv = useCallback(async () => {
    Swal.fire({
      title: 'Exporta dados!',
      html: 'Aguarde um momento<b></b>.',
      timerProgressBar: true,
      timer: 99999,
      didOpen: () => {
        Swal.showLoading()
      }
    })
    const purshase = data

    let obj = {}
    if (params.fornecedoresIds.length === 1) {
      obj = {
        recomendacaoFornecimento: purshase
      }
    } else {
      obj = { recomendacaoFornecimento: purshase }
    }
    try {
      const { data: res } = await mrp.postReabExportSapCsv({
        params: obj
      })
      fileDownload(res, filename + '.csv')

      Swal.close()
    } catch (error: any) {
      const { data } = error.response
      const message =
        data.response?.status === 400
          ? data.response?.data?.data[0].message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'error',
        duration: 2000,
        isClosable: true
      })
      Swal.close()
    }
  }, [data, params.fornecedoresIds.length, filename, toast])

  const getData = useCallback(async () => {
    // await getMaintence()

    setLoading(true)
    let newParams = params
    if (localStorage.getItem('mrpReabParams')) {
      newParams = JSON.parse(
        crypto.AES.decrypt(
          localStorage.getItem('mrpReabParams'),
          'CHAVE'
        ).toString(crypto.enc.Utf8) || ''
      )
      updateParams(newParams)
    }

    const paramsToSend = {
      produtosIds: paramsHelper.parse(newParams.produtosIds),
      fornecedoresIds: paramsHelper.parse(newParams.fornecedoresIds),
      lojasIds: paramsHelper.parse(newParams.lojasIds),
      empresaIds: paramsHelper.parse(newParams.empresaIds),
      curvaABC: newParams.curvaAbc.length > 0 ? newParams.curvaAbc[0].value : ''
    }

    try {
      const { data: res } = await mrp.getReabData({
        page: 1,
        params: paramsToSend
      })

      const { data } = res

      updateShowTableInTable(data.grid)

      updateData(draft => {
        return draft.filter(item => item.status === 99).concat(data.grid)
      })

      updateDataCards(data.resumo)
      if (params.fornecedoresIds.length === 1) {
        getDiscountBySupply()
      }
      setFilters.toggle()
      register({
        name: 'MRP Reab. Emergencial',
        path: '/mrp/reabastecimento-emergencial',
        loading: false
      })
    } catch {
      setFilters.toggle()
      updateData([])
      updateDataCards({
        produtos: 0,
        peso: 0,
        indicadorCurva: []
      })
    }

    setLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params])

  const handleLocalParams = useCallback(async () => {
    if (localStorage.getItem('mrpReabParams')) {
      const newParams = JSON.parse(
        crypto.AES.decrypt(
          localStorage.getItem('mrpReabParams'),
          'CHAVE'
        ).toString(crypto.enc.Utf8) || ''
      )

      updateParams(newParams)
    } else {
      localStorage.setItem(
        'mrpReabParams',
        crypto.AES.encrypt(JSON.stringify(params), 'CHAVE').toString()
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateParams, data?.length, params])

  const handleUpdateSapParams = ({ name, value }: IUpdateParamsSap) => {
    updateSapParams(draft => {
      draft[name] = value
    })
    if (name === 'metodoPagamento') {
      if (discountSupply.metodoPagamentoId === value[0]?.value) {
        updateSapParams(draft => {
          draft.desconto = discountSupply.desconto.toString()
        })
      }
    }
    if (
      paramsSap.metodoPagamento[0]?.value === discountSupply.metodoPagamentoId
    ) {
      if (name === 'desconto') {
        if (value < discountSupply.desconto) {
          updateSapParams(draft => {
            draft.desconto = discountSupply.desconto.toString()
          })
        }
      }
    }
  }
  const getDiscountBySupply = useCallback(async () => {
    try {
      const { data: res } = await generic.getDiscountSupply({
        fornecedoresIds: [
          params.fornecedoresIds.length > 0
            ? params.fornecedoresIds[0].value
            : 0
        ]
      })

      const { data } = res

      updateDiscountSupply(data[0])
      // const newParamsSap = paramsSap
      // updateSapParams({ ...newParamsSap, desconto: data[0].desconto })
    } catch {}
  }, [updateDiscountSupply, params])

  const getSupplyCommands = useCallback(async () => {
    try {
      const { data: res } = await generic.getSupplyMethods()

      const { data } = res

      updateSupplyMethod(data)
    } catch {}
    setLoading(false)
  }, [updateSupplyMethod])

  useEffect(() => {
    const fetchSapFlag = async () => {
      const flag = await isSapExportFlag()
      setSapFlag(flag)
    }
    fetchSapFlag()
  }, [])

  const parsedData = useMemo(() => parseReabMrpData(data), [data])

  const contextValue = useMemo(
    () => ({
      loading,
      fetching,

      filtersList,
      tableInteraction,

      data: parsedData,
      actions,
      params,
      calc,

      headers,

      filename,
      query,
      filters,
      discountSupply,
      toggleColumn,
      handleUpdateWanted,
      handleUpdateParams,
      clearAllFilters,

      getData,
      dataCards,
      handleLocalParams,
      getSupplyCommands,
      exportData,
      exportDataSap,

      setQuery,
      setReady,
      setFilters,
      setFilename,
      handleUpdateSapParams,
      exportDataSapCsv,
      paramsSap,
      supplyMethod
    }),
    [
      loading,
      fetching,
      filtersList,
      tableInteraction,
      parsedData,
      actions,
      params,
      calc,
      headers,
      filename,
      query,
      filters,
      discountSupply,
      toggleColumn,
      handleUpdateWanted,
      handleUpdateParams,
      clearAllFilters,
      getData,
      dataCards,
      handleLocalParams,
      getSupplyCommands,
      exportData,
      exportDataSap,
      setQuery,
      setReady,
      setFilters,
      setFilename,
      handleUpdateSapParams,
      exportDataSapCsv,
      paramsSap,
      supplyMethod
    ]
  )

  return (
    <MrpReabContext.Provider value={contextValue}>
      {children}
      <SapHistoricModalWrapper
        orderCode={currentOrderCode}
        onModalClosed={handleClearOrderCode}
      />
      <ReabPeriodChart
        title="Reabastecimento Emergencial"
        list={data}
        selected={selected}
        setSelected={setSelected}
      />
    </MrpReabContext.Provider>
  )
}

export default MrpReabProvider
export const MrpReabContext = createContext<IMrpReabContext>(
  {} as IMrpReabContext
)
